import { PriceChartPanel } from "./price-chart-panel.ts";

// The 4 economic price panels, each a configured PriceChartPanel.
export function createGoldIntlPanel(): PriceChartPanel {
  return new PriceChartPanel({
    id: "gold-intl",
    title: "Vàng thế giới",
    subtitle: "XAU/USD",
    valueFormat: "usd",
    seriesDefs: [{ series: "xau-usd", label: "Vàng (oz)", color: "#f5c518", field: "price" }],
    projection: true, // reference-only 14-day trend overlay (single series, 1y history)
  });
}

export function createGoldVnPanel(): PriceChartPanel {
  return new PriceChartPanel({
    id: "gold-vn",
    title: "Vàng trong nước",
    subtitle: "SJC / nhẫn — bán ra",
    valueFormat: "vnd-gold",
    // SJC bán ra + nhẫn bán ra (nhẫn forward-filled from launch — sparse until history builds).
    seriesDefs: [
      { series: "sjc-bar", label: "SJC bán", color: "#f5c518", field: "sell" },
      { series: "pnj-ring", label: "Nhẫn bán", color: "#7cc4ff", field: "sell" },
    ],
  });
}

export function createOilPanel(): PriceChartPanel {
  return new PriceChartPanel({
    id: "oil",
    title: "Dầu thô",
    subtitle: "Brent / WTI",
    valueFormat: "usd",
    seriesDefs: [
      { series: "brent", label: "Brent", color: "#4ade80", field: "price" },
      { series: "wti", label: "WTI", color: "#f97316", field: "price" },
    ],
  });
}

export function createFxPanel(): PriceChartPanel {
  return new PriceChartPanel({
    id: "fx",
    title: "Tỷ giá",
    subtitle: "USD / EUR → VND",
    valueFormat: "vnd-fx",
    seriesDefs: [
      { series: "usd-vnd", label: "USD", color: "#7cc4ff", field: "price" },
      { series: "eur-vnd", label: "EUR", color: "#c084fc", field: "price" },
    ],
  });
}

// Join two series by UTC-day so a derived point uses same-day values. Returns a map
// keyed by day-ms → {a, b}. Used to align SJC (buy/sell) with quy đổi (price).
function joinByDay(
  a: { ts: number; sell: number | null }[],
  b: { ts: number; price: number | null }[],
): { ts: number; a: number; b: number }[] {
  const dayMs = (ts: number) => {
    const d = new Date(ts);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  const bByDay = new Map<number, number>();
  for (const p of b) if (p.price != null) bByDay.set(dayMs(p.ts), p.price);
  const out: { ts: number; a: number; b: number }[] = [];
  for (const p of a) {
    if (p.sell == null) continue;
    const day = dayMs(p.ts);
    const bv = bByDay.get(day);
    if (bv != null && bv > 0) out.push({ ts: day, a: p.sell, b: bv });
  }
  return out;
}

// Premium % — how far SJC sits above world gold converted to VND ("căng thẳng" of the
// domestic gold market). premium = SJC_sell / quy_đổi − 1. Sanity-clamped to [0,60%]
// so a bad row can't produce a wild line. Forward-only until SJC history builds.
export function createGoldPremiumPanel(): PriceChartPanel {
  return new PriceChartPanel({
    id: "gold-premium",
    title: "Chênh lệch vàng SJC",
    subtitle: "SJC vs vàng thế giới quy đổi",
    valueFormat: "percent",
    seriesDefs: [],
    caption:
      "Chênh lệch = giá SJC bán ra ÷ vàng thế giới quy đổi − 1. Phản ánh mức căng thẳng của thị trường vàng trong nước. Không phải khuyến nghị.",
    derive: {
      inputs: ["sjc-bar", "xau-vnd-quydoi"],
      label: "Chênh lệch",
      color: "#f97316",
      compute: (raw) => {
        const joined = joinByDay(
          (raw["sjc-bar"] ?? []).map((p) => ({ ts: p.ts, sell: p.sell })),
          (raw["xau-vnd-quydoi"] ?? []).map((p) => ({ ts: p.ts, price: p.price })),
        );
        // Clamp (not drop) to [-20,100]% so a single corrupt row can't spike the line,
        // while still showing genuine stress spikes — VN SJC premium has historically
        // exceeded 40%, so a hard 60% *filter* would hide real data as a gap.
        return joined.map((j) => ({
          ts: j.ts,
          value: Math.max(-20, Math.min(100, (j.a / j.b - 1) * 100)),
        }));
      },
    },
  });
}

// Simple trailing moving average over derived points (window in points).
function movingAverage(pts: { ts: number; value: number }[], window: number): { ts: number; value: number }[] {
  const out: { ts: number; value: number }[] = [];
  for (let i = 0; i < pts.length; i++) {
    if (i < window - 1) continue;
    let sum = 0;
    for (let j = i - window + 1; j <= i; j++) sum += pts[j].value;
    out.push({ ts: pts[i].ts, value: sum / window });
  }
  return out;
}

// VN-Index — daily close (2y history) with a 20-day moving-average overlay.
export function createVnIndexPanel(): PriceChartPanel {
  const closes = (raw: Record<string, { ts: number; price: number | null }[]>) =>
    (raw["vnindex"] ?? [])
      .filter((p) => p.price != null)
      .map((p) => ({ ts: p.ts, value: p.price as number }));
  return new PriceChartPanel({
    id: "vnindex",
    title: "VN-Index",
    subtitle: "chỉ số + MA20",
    valueFormat: "points",
    seriesDefs: [],
    caption: "Chỉ số VN-Index (điểm) và trung bình trượt 20 phiên. Nguồn: VNDirect.",
    derive: {
      inputs: ["vnindex"],
      label: "VN-Index",
      color: "#4ade80",
      compute: (raw) => closes(raw),
      overlay: {
        label: "MA20",
        color: "#9aa4b2",
        compute: (raw) => {
          const c = closes(raw).sort((a, b) => a.ts - b.ts);
          return movingAverage(c, 20);
        },
      },
    },
  });
}
